import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../../store/slices/userSlice';
import './UserProfile.css';
import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import cameraIcon from '../../../assets/profile/cameraIcon.png';
import Modal from '../../../components/Modal/Modal';
import profileDefault from '../../../assets/profileDefault.png';
// services
import { authService } from '../../../services/login';
import { usersList } from '../../../services/user';

const UserProfile = () => {
  const URLProfile = process.env.REACT_APP_PROFILE_URL;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile, loading, error } = useSelector((state) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [title, setTitle] = useState({ text: 'Successful', highlight: 'change' });
  const [message, setMessage] = useState('For security reasons you must log in again');
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    id: '',
    user: '',
    phone: '',
    email: '',
    role: ''
  });

  // En UserProfile.js - actualiza handleFileSelect
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar el tipo de archivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Por favor selecciona una imagen válida (PNG, JPG o JPEG)');
      return;
    }
    try {
      // Mostrar la imagen seleccionada inmediatamente
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
      const formDataImage = new FormData();
      formDataImage.append('file', file);
      const response = await usersList.updateProfilePhoto(formDataImage, formData.id);
      if (response && response.status === 'success') {
        // Actualizar el estado local
        setTitle({ text: 'Photo', highlight: 'changed' });
        setMessage('Successfully changed');
        setIsModalOpen(true);
        
        // Actualizar Redux con la nueva foto
        const updatedUserData = {
          ...formData,
          profile: {
            ...formData.profile,
            profile_photo: response.data.profile_photo
          }
        };
        dispatch(updateUser(updatedUserData));
      } else {
        throw new Error(response?.message || 'Error al actualizar la foto');
      }
    } catch (error) {
      console.error('Error al actualizar la foto:', error);
      // Revertir la imagen si falla
      setSelectedImage(null);
    } finally {
      // Limpiar el input de archivo para permitir seleccionar el mismo archivo nuevamente
      event.target.value = '';
    }
  };

  const handleChange = (e, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    // Required field validation
    ['email', 'phone'].forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
        hasErrors = true;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }
    if (!formData.email.endsWith('@barterflip.com')) {
      newErrors.email = 'The email must be @barterflip.com';
      hasErrors = true;
    }

    // Phone validation
    if(formData.phone.trim().length !== 10) {
      newErrors.phone = 'Please enter a valid phone number (10 digits)';
      hasErrors = true;
    }
    if (formData.phone && !/^\+?[\d\s-]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        const response = await usersList.updateUser(formData);
        if (response && response.status === 'success') {
          setTitle({ text: 'Profile', highlight: 'updated' });
          setMessage('Successfully updated');
          setIsModalOpen(true);
        } else {
          throw new Error(response?.message || 'Error al actualizar el perfil');
        }
      } catch (error) {
        console.error('Error al actualizar el perfil:', error);
        setTitle({ text: 'Profile', highlight: 'update' });
        setMessage('Failed to update');
        setIsModalOpen(true);
      }
    } else {
      console.log('Form validation failed');
    }
  };

  const handlePasswordChange = () => {
    navigate('/profile/change-password', { state: {
      user: formData,
      profile_photo: selectedImage
    } });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await authService.getDataUser();

        if (response.data.data) {
          const userData = response.data.data;
          let role = userData.roles[0] || 'Not assigned';
          role = role === "super_admin" ? "Super Admin" : role;
          setFormData({
            id: userData.id,
            name: userData.name,
            user: userData.user,
            phone: userData.phone,
            email: userData.email,
            role: role
          });
          if(userData.profile?.profile_photo) {
            setSelectedImage(URLProfile + userData.profile?.profile_photo);
            console.log(URLProfile + userData.profile?.profile_photo);
          }
        } else {
          throw new Error('No se recibieron datos del usuario');
        }
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
        alert('Error al cargar los datos del usuario');
      }
    };
    fetchUserData();
  }, []);

  return (
    <div className="user-profile-page">
      <div className="profile-card">
        <div className="profile-banner">
          <div className="profile-photo-container">
            <img 
              src={selectedImage || profileDefault} 
              alt="Foto de perfil" 
              className="profile-photo"
            />
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileSelect}
            />
            <button 
              className="edit-photo-btn"
              onClick={() => fileInputRef.current.click()}
            >
              <span className="camera-icon">
                <img src={cameraIcon} alt="Camera" className="camera-icon" />
              </span>
            </button>
          </div>
        </div>

        <div className="profile-info">
          <h2 className="profile-name">{formData.name}</h2>
          <p className="profile-role">{formData.role}</p>

          <div className="profile-details">
            <div className="detail-group">
              <label htmlFor="user">User</label>
              <Input
                type="text"
                placeholder="User"
                value={formData.user}
                onChange={(e) => handleChange(e, 'user')}
                required
                disabled={true}
              />
            </div>


            <div className="detail-group">
              <label>Phone</label>
              <Input
                type="tel"
                placeholder="Enter your phone"
                value={formData.phone}
                onChange={(e) => handleChange(e, 'phone')}
                error={errors.phone}
                required
                size="small"
              />
            </div>

            <div className="detail-group">
              <label>Email</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleChange(e, 'email')}
                error={errors.email}
                required
                size="small"
              />
            </div>
          </div>

          <div className="profile-actions">
            <span 
              onClick={handlePasswordChange}
              className="change-password-link"
              role="button"
              tabIndex={0}
            >
              Change your password
            </span>
            <Button 
              onClick={handleSave}
              type="submit"
              fullWidth
            >
              Save
            </Button>
            <Modal 
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              title={title}
              message={message}
              onConfirm={handleModalClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
